import React, { useState, useEffect } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Default export React component
export default function BankWalaSelfAssessment() {
  // 8-Pillar Bank Employee Assessment
  const baseQuestions = [
    // 1. Digital Banking & FinTech Awareness
    {
      id: "q1",
      label: "I understand the key digital banking products (UPI, BBPS, video KYC, digital onboarding, mobile/internet banking).",
      pillar: "Digital Banking & FinTech Awareness",
    },
    {
      id: "q2",
      label: "I actively stay updated on new FinTech trends and how they impact banking and customer expectations.",
      pillar: "Digital Banking & FinTech Awareness",
    },
    {
      id: "q3",
      label: "I can confidently guide customers to use digital channels and resolve basic issues related to them.",
      pillar: "Digital Banking & FinTech Awareness",
    },

    // 2. AI & Technology Readiness
    {
      id: "q4",
      label: "I understand how AI/automation tools (bots, scoring engines, workflows) are being used in banking.",
      pillar: "AI & Technology Readiness",
    },
    {
      id: "q5",
      label: "I can efficiently use bank technology platforms (CRM, LOS, LMS, dashboards, core banking front-ends) required in my role.",
      pillar: "AI & Technology Readiness",
    },
    {
      id: "q6",
      label: "I am open to learning new tools and adapting quickly when processes or systems change.",
      pillar: "AI & Technology Readiness",
    },

    // 3. Regulatory & Compliance Readiness
    {
      id: "q7",
      label: "I stay updated with important RBI guidelines, internal circulars, and policy changes that impact my work.",
      pillar: "Regulatory & Compliance Readiness",
    },
    {
      id: "q8",
      label: "I follow KYC, AML, documentation and compliance processes accurately without shortcuts.",
      pillar: "Regulatory & Compliance Readiness",
    },
    {
      id: "q9",
      label: "I maintain audit-ready records and can quickly produce supporting documents when required.",
      pillar: "Regulatory & Compliance Readiness",
    },

    // 4. Credit, Risk & Operational Discipline
    {
      id: "q10",
      label: "I understand basic credit principles and can evaluate customer needs responsibly for my product set.",
      pillar: "Credit, Risk & Operational Discipline",
    },
    {
      id: "q11",
      label: "I strictly follow maker-checker, approvals, and operational checklists without skipping steps.",
      pillar: "Credit, Risk & Operational Discipline",
    },
    {
      id: "q12",
      label: "I am able to identify early warning signals or risk flags and escalate issues proactively.",
      pillar: "Credit, Risk & Operational Discipline",
    },

    // 5. Customer Service & Relationship Skills
    {
      id: "q13",
      label: "I communicate clearly, patiently, and respectfully with customers, even in high-pressure situations.",
      pillar: "Customer Service & Relationship Skills",
    },
    {
      id: "q14",
      label: "I can identify cross-sell and up-sell opportunities and position products appropriately to customers.",
      pillar: "Customer Service & Relationship Skills",
    },
    {
      id: "q15",
      label: "I take ownership of customer queries and ensure proper follow-up until resolution.",
      pillar: "Customer Service & Relationship Skills",
    },

    // 6. Cybersecurity Hygiene
    {
      id: "q16",
      label: "I follow safe digital practices like strong passwords, locking my system, and not leaving data exposed.",
      pillar: "Cybersecurity Hygiene",
    },
    {
      id: "q17",
      label: "I am careful with emails/links/attachments and can recognize potential phishing or fraud attempts.",
      pillar: "Cybersecurity Hygiene",
    },
    {
      id: "q18",
      label: "I understand the importance of data privacy and avoid sharing customer or internal data casually.",
      pillar: "Cybersecurity Hygiene",
    },

    // 7. Personal Productivity & Work Management
    {
      id: "q19",
      label: "I plan my day, prioritize tasks, and use some structured method or tool to track my work.",
      pillar: "Personal Productivity & Work Management",
    },
    {
      id: "q20",
      label: "I maintain discipline in reporting, documentation, and closure of daily tasks.",
      pillar: "Personal Productivity & Work Management",
    },
    {
      id: "q21",
      label: "I generally meet my targets and timelines without frequent last-minute rush or crisis.",
      pillar: "Personal Productivity & Work Management",
    },

    // 8. Continuous Learning & Growth Mindset
    {
      id: "q22",
      label: "I actively engage in learning through internal training, e-modules, circulars, or external resources.",
      pillar: "Continuous Learning & Growth Mindset",
    },
    {
      id: "q23",
      label: "I reflect on my mistakes or feedback and consciously try to improve my working style.",
      pillar: "Continuous Learning & Growth Mindset",
    },
    {
      id: "q24",
      label: "I take initiative beyond minimum job expectations to grow in my role and career.",
      pillar: "Continuous Learning & Growth Mindset",
    },
  ];

  const initialAnswers = {};
  baseQuestions.forEach((q) => (initialAnswers[q.id] = 1));

  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem("bankwala_self_assessment_answers");
      return saved ? JSON.parse(saved) : initialAnswers;
    } catch (e) {
      return initialAnswers;
    }
  });

  useEffect(() => {
    localStorage.setItem("bankwala_self_assessment_answers", JSON.stringify(answers));
  }, [answers]);

  const handleChange = (id, value) => {
    setAnswers((s) => ({ ...s, [id]: Number(value) }));
  };

  const totalScore = () => {
    return Object.values(answers).reduce((a, b) => a + b, 0);
  };

  const maxScore = () => {
    return baseQuestions.length * 5; // 24 * 5 = 120
  };

  const levelFromScore = (score) => {
    const max = maxScore();
    const scaled = Math.round((score / max) * 75); // keep original scaling logic

    if (scaled <= 25) return { level: 1, name: "Level 1 – Basic Awareness" };
    if (scaled <= 40) return { level: 2, name: "Level 2 – Functional" };
    if (scaled <= 55) return { level: 3, name: "Level 3 – Proficient" };
    if (scaled <= 65) return { level: 4, name: "Level 4 – Advanced" };
    return { level: 5, name: "Level 5 – Future-Ready" };
  };

  const getRecommendations = (level) => {
    const map = {
      1: [
        "Focus on basics: attend foundational sessions on digital banking, compliance, and core products.",
        "Use daily checklists for operations, documentation, and customer handling.",
        "Spend at least 10–15 minutes daily on BankWala Learning modules to build clarity.",
      ],
      2: [
        "Start tracking your tasks and follow-ups using simple tools or the Goal Board.",
        "Strengthen compliance discipline: re-check KYC, AML, and documentation protocols.",
        "Practice better customer conversations using provided scripts and case studies.",
      ],
      3: [
        "Identify 1–2 areas (credit, sales, service, or tech) where you can become a ‘go-to person’.",
        "Participate in AI, analytics or advanced digital banking modules on BankWala.",
        "Start mentoring juniors or peers informally in topics you are strong in.",
      ],
      4: [
        "Lead small improvement projects in your branch (TAT reduction, customer service upgrades, digital adoption).",
        "Deepen your understanding of risk, cybersecurity, and advanced compliance topics.",
        "Prepare for leadership roles with structured learning paths and feedback loops.",
      ],
      5: [
        "Take up cross-functional exposure (credit, risk, operations, business) to build strategic capability.",
        "Experiment with FinTech/AI tools where allowed and share learnings with your team.",
        "Act as a role model: drive a culture of learning, ethics, and disciplined execution.",
      ],
    };
    return map[level] || [];
  };

  const pillarScores = () => {
    const pillarMap = {};
    const pillarCounts = {};

    baseQuestions.forEach((q) => {
      pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + answers[q.id];
      pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1;
    });

    const data = Object.keys(pillarMap).map((p) => ({
      subject: p,
      A: +(pillarMap[p] / pillarCounts[p]).toFixed(2),
      fullMark: 5,
    }));
    return data;
  };

  const resetAll = () => {
    const ia = {};
    baseQuestions.forEach((q) => (ia[q.id] = 1));
    setAnswers(ia);
    localStorage.removeItem("bankwala_self_assessment_answers");
  };

  const exportJSON = () => {
    const payload = {
      answers,
      total: totalScore(),
      max: maxScore(),
      level: levelFromScore(totalScore()),
      date: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bankwala_self_assessment.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const score = totalScore();
  const levelObj = levelFromScore(score);
  const recs = getRecommendations(levelObj.level);
  const chartData = pillarScores();

  // Prepare data for Chart.js Radar
  const labels = chartData.map((d) => d.subject);
  const dataPoints = chartData.map((d) => d.A);

  const radarData = {
    labels,
    datasets: [
      {
        label: "Average Pillar Score (1–5)",
        data: dataPoints,
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderColor: "#10b981",
        borderWidth: 2,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#10b981",
        pointHoverBackgroundColor: "#10b981",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          backdropColor: "transparent",
        },
        grid: {
          color: "rgba(148, 163, 184, 0.3)",
        },
        pointLabels: {
          font: {
            size: 11,
          },
          color: "#e5e7eb",
        },
        angleLines: {
          color: "rgba(148, 163, 184, 0.3)",
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "#e5e7eb",
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.r}`;
          },
        },
      },
    },
  };

  return (
    <div className="p-6 bg-black mx-auto text-white">
      <h1 className="text-xl lg:text-2xl font-bold text-yellow-200 mb-2">
        BankWala — 8-Pillar Banking Competency Check
      </h1>
      <p className="text-sm text-gray-200 mb-4">
        Rate yourself on a scale of 1–5 for each statement. Get your overall score, maturity level,
        pillar-wise strengths, and practical recommendations for becoming a future-ready banker.
      </p>

      <div className="bg-gray-900 border border-blue-500 shadow rounded-lg p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 lg:gap-0">
          <div className="flex flex-col">
            <div className="text-sm text-gray-200">Total Score</div>
            <div className="text-xl lg:text-3xl font-extrabold text-white">
              {score} / {maxScore()}
            </div>
            <div className="text-sm mt-1 text-emerald-300">{levelObj.name}</div>
          </div>

          <div className="flex flex-col lg:flex-row items-end lg:items-center gap-2 self-end lg:self-auto">
            <button
              onClick={resetAll}
              className="px-3 py-1 rounded border border-gray-500 text-xs lg:text-sm hover:bg-gray-800"
            >
              Reset
            </button>
            <button
              onClick={exportJSON}
              className="px-3 py-1 w-full lg:w-48 rounded bg-emerald-500 text-xs lg:text-sm text-white hover:bg-emerald-600"
            >
              Export JSON
            </button>
          </div>
        </div>
      </div>

      <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {baseQuestions.map((q) => (
          <div
            key={q.id}
            className="bg-black border border-blue-500/70 p-4 rounded shadow-sm"
          >
            <label className="block text-sm font-medium mb-2">{q.label}</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 2, 3, 4, 5].map((n) => (
                <label
                  key={n}
                  className={`inline-flex items-center cursor-pointer text-xs lg:text-sm px-1 ${
                    answers[q.id] === n ? "font-semibold text-emerald-300" : "text-gray-400"
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={n}
                    checked={answers[q.id] === n}
                    onChange={(e) => handleChange(q.id, e.target.value)}
                    className="mr-1 accent-emerald-500"
                  />
                  {n}
                </label>
              ))}
            </div>
            <div className="text-[0.7rem] text-gray-400 mt-2">
              Pillar: <span className="text-gray-200">{q.pillar}</span>
            </div>
          </div>
        ))}
      </form>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1 md:col-span-2 bg-gray-900 border border-blue-500 p-4 rounded shadow">
          <h3 className="font-semibold mb-2 text-blue-300 text-sm lg:text-base">
            Pillar Radar (Average Score 1–5)
          </h3>
          <div style={{ position: "relative", height: "320px", width: "100%" }}>
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        <div className="bg-black border border-blue-500 p-4 rounded shadow">
          <h3 className="font-semibold mb-2 text-sm lg:text-base text-blue-300">Recommendations</h3>
          <div className="text-xs lg:text-sm text-gray-200 space-y-2">
            <div className="font-medium text-emerald-300">Stage: {levelObj.name}</div>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {recs.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
            <div className="mt-4">
              <button
                onClick={() => window.print()}
                type="button"
                className="px-3 py-1 rounded border border-gray-500 text-xs hover:bg-gray-800"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 text-[0.7rem] text-gray-500">
        Tip: Re-take this assessment every 3–6 months. Compare your radar chart and JSON exports to
        track your journey toward becoming a future-ready banker.
      </div>
    </div>
  );
}
